import {
  BanknoteIcon,
  BookTextIcon,
  CalendarIcon,
  MapPinnedIcon,
  NetworkIcon,
  PackageCheckIcon,
  ReceiptIcon,
  TagIcon,
  TruckIcon,
  UserRoundIcon,
} from 'lucide-react';
import { Link } from 'react-router';

import type { OrderDTO } from '@invenco/sales-interface/interfaces';
import type { ShipmentDTO } from '@invenco/shipping-interface';

import { AddressDisplay } from '@/components/address-display';
import { Button } from '@/components/core/button';
import { Skeleton } from '@/components/core/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/core/tabs';
import { DetailGroup } from '@/components/detail-group';
import { DetailList } from '@/components/detail-list';
import { EMPTY_TEXT_VALUE } from '@/constants';
import { formatDate } from '@/shared/helpers';

import { addressesMatch } from '../customer-fields';
import { OrderTags } from './order-tags';
import { OrderTimeline } from './order-timeline/order-timeline';

type Props = {
  order?: OrderDTO;
  shipments?: ShipmentDTO[];
  isLoading?: boolean;
};

function contactMatches(first?: string | null, second?: string | null) {
  return (first ?? '').trim() === (second ?? '').trim();
}

function getDisplayAddress(
  order: OrderDTO | undefined,
  address?: OrderDTO['shippingAddress'] | OrderDTO['billingAddress'],
) {
  if (!address) return undefined;

  const { contactName, contactEmail, contactNumber, ...addressFields } = address;

  return {
    ...addressFields,
    ...(contactMatches(contactName, order?.customerName) ? {} : { contactName }),
    ...(contactMatches(contactEmail, order?.email) ? {} : { contactEmail }),
    ...(contactMatches(contactNumber, order?.phone) ? {} : { contactNumber }),
  };
}

function getChannelValue(order?: OrderDTO) {
  if (!order?.channelName) return undefined;

  if (!order.channelId) return order.channelName;

  return (
    <Button variant="link" size="inline" asChild>
      <Link to={`/sales/channels/${order.channelId}`}>{order.channelName}</Link>
    </Button>
  );
}

function getCustomerValue(order?: OrderDTO) {
  if (!order) return undefined;

  const contactLines = [order.email, order.phone].filter(Boolean);
  if (!order.customerName && contactLines.length === 0) return undefined;

  return (
    <div className="space-y-1">
      {order.customerName ? <div>{order.customerName}</div> : null}
      {contactLines.map((line) => (
        <div key={line} className="font-normal text-muted-foreground">
          {line}
        </div>
      ))}
    </div>
  );
}

function getAddressValue(
  order: OrderDTO | undefined,
  address?: OrderDTO['shippingAddress'] | OrderDTO['billingAddress'],
) {
  if (!address) return undefined;

  return <AddressDisplay address={getDisplayAddress(order, address)} compact />;
}

function DetailsGroup({ order, isLoading }: { order?: OrderDTO; isLoading?: boolean }) {
  const isBillingSameAsShipping = Boolean(
    !isLoading &&
    order?.billingAddress &&
    addressesMatch(order.shippingAddress, order.billingAddress),
  );

  return (
    <DetailGroup>
      <DetailList
        title="Customer"
        isLoading={isLoading}
        items={[
          { icon: UserRoundIcon, label: 'Name', value: getCustomerValue(order) },
          {
            icon: MapPinnedIcon,
            label: 'Shipping address',
            value: getAddressValue(order, order?.shippingAddress),
          },
          {
            icon: ReceiptIcon,
            label: 'Billing address',
            value: isBillingSameAsShipping ? (
              <span className="text-muted-foreground">Same as shipping address</span>
            ) : (
              getAddressValue(order, order?.billingAddress)
            ),
          },
        ]}
      />
      <DetailList
        title="Properties"
        isLoading={isLoading}
        items={[
          {
            icon: CalendarIcon,
            label: 'Order placed',
            value: order?.orderedAt ? formatDate(order.orderedAt) : undefined,
          },
          { icon: NetworkIcon, label: 'Channel', value: getChannelValue(order) },
          { icon: BanknoteIcon, label: 'Currency', value: order?.currency },
          {
            icon: PackageCheckIcon,
            label: 'Partial fulfillment',
            value: order?.allowPartialFulfillments ? 'Enabled' : 'Disabled',
          },
          {
            icon: MapPinnedIcon,
            label: 'Fulfillment profile',
            value: order?.fulfillmentProfileName,
          },
          { icon: TruckIcon, label: 'Shipping profile', value: order?.shippingProfileName },
        ]}
      />
      <DetailList
        title="More"
        isLoading={isLoading}
        items={[
          {
            icon: BookTextIcon,
            label: 'Reference',
            value: order?.reference ? (
              <span className="whitespace-pre-wrap">{order.reference}</span>
            ) : undefined,
          },
          {
            icon: TagIcon,
            label: 'Tags',
            value: order?.tags?.length ? <OrderTags tags={order.tags} /> : undefined,
          },
        ]}
      />
    </DetailGroup>
  );
}

export function OrderDetailTabs({ order, shipments, isLoading }: Props) {
  return (
    <Tabs defaultValue="details" className="gap-5">
      <TabsList variant="button" aria-label="Order details">
        <TabsTrigger value="details">Details</TabsTrigger>
        <TabsTrigger value="timeline">Timeline</TabsTrigger>
        <TabsTrigger value="notes">Notes</TabsTrigger>
      </TabsList>
      <TabsContent value="details">
        <DetailsGroup order={order} isLoading={isLoading} />
      </TabsContent>
      <TabsContent value="timeline">
        <OrderTimeline order={order} shipments={shipments} isLoading={isLoading} />
      </TabsContent>
      <TabsContent value="notes">
        {isLoading ? (
          <Skeleton className="h-4 w-full" aria-label="Loading value" />
        ) : (
          <p className="wrap-break-word whitespace-pre-wrap">
            {order?.notes ?? <span className="text-muted-foreground">{EMPTY_TEXT_VALUE}</span>}
          </p>
        )}
      </TabsContent>
    </Tabs>
  );
}
